// src/pages/AddQuestionPage.jsx
import React, { useState, useEffect } from 'react';
import BackButton from '../components/BackButton';
import {
  TextField,
  Button,
  MenuItem,
  Alert,
  FormControl,
  InputLabel,
  Select,
  Box,
  Typography
} from '@mui/material';
import axios from 'axios';

const decodeJwtPayload = (token) => {
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
};

const AddQuestionPage = () => {
  const [content, setContent] = useState('');
  const [optionA, setOptionA] = useState('');
  const [optionB, setOptionB] = useState('');
  const [optionC, setOptionC] = useState('');
  const [optionD, setOptionD] = useState('');
  const [correctOption, setCorrectOption] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [lessonId, setLessonId] = useState('');
  const [lessons, setLessons] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const token = localStorage.getItem('teacherToken');

  useEffect(() => {
    if (!token) {
      setError('توکن یافت نشد. لطفاً دوباره وارد شوید.');
      return;
    }

    const fetchLessons = async () => {
      try {
        const res = await axios.get('https://education-backend.liara.run/api/lesson/by-teacher', {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          validateStatus: s => s < 500
        });
        if (res.status === 200) {
          const arr = Array.isArray(res.data) ? res.data : (res.data?.data ?? res.data?.items ?? []);
          setLessons(arr);
        } else {
          setLessons([]);
          setError(res.data?.message || res.data?.title || 'خطا در دریافت لیست درس‌ها');
        }
      } catch (err) {
        setLessons([]);
        setError('خطا در دریافت لیست درس‌ها. اتصال شبکه را بررسی کنید.');
      }
    };

    fetchLessons();
  }, [token]);

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    if (!lessonId || !content || !correctOption) {
      setError('لطفاً همهٔ فیلدهای ضروری را پر کنید (درس، متن سؤال، گزینه صحیح).');
      return;
    }

    const jwt = token ? decodeJwtPayload(token) : null;
    const teacherIdFromToken = jwt?.id || jwt?.teacherId || jwt?.sub || undefined;

    const expIso = expirationDate ? (new Date(expirationDate).toISOString()) : new Date().toISOString();

    const payload = {
      content: content.trim(),
      optionA: optionA.trim(),
      optionB: optionB.trim(),
      optionC: optionC.trim(),
      optionD: optionD.trim(),
      correctOption: String(correctOption).trim().slice(0, 1),
      expirationDate: expIso,
      lessonId: Number(lessonId),
      ...(teacherIdFromToken ? { teacherId: Number(teacherIdFromToken) } : {})
    };

    try {
      const res = await axios.post('https://education-backend.liara.run/api/question/create', payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        validateStatus: s => s < 500
      });

      if (res.status >= 400) {
        setError(res.data?.message || res.data?.title || 'ثبت سؤال موفق نبود.');
        return;
      }

      setSuccess('✅ سؤال با موفقیت ثبت شد.');
      setContent(''); setOptionA(''); setOptionB(''); setOptionC(''); setOptionD('');
      setCorrectOption(''); setExpirationDate(''); setLessonId('');
    } catch (err) {
      setError(err.response?.data?.message || 'خطای شبکه در ثبت سؤال');
    }
  };

  return (
    <Box sx={{ padding: '2rem', maxWidth: '600px', margin: 'auto' }} dir="rtl">
      <header style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <BackButton toFallback="/dashboard" />
        </header>
      <Typography variant="h5" gutterBottom>📝 ثبت سؤال جدید</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <TextField
        label="درس مرتبط"
        select
        value={lessonId}
        onChange={(e) => setLessonId(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      >
        <MenuItem value="">انتخاب کنید</MenuItem>
        {lessons.map((lesson) => (
          <MenuItem key={lesson.Id ?? lesson.id ?? lesson._id} value={lesson.Id ?? lesson.id ?? lesson._id}>
            {lesson.Title ?? lesson.title ?? lesson.name}
          </MenuItem>
        ))}
      </TextField>

      <TextField label="متن سؤال" value={content} onChange={(e) => setContent(e.target.value)} fullWidth multiline rows={3} sx={{ mb: 2 }} />

      <TextField label="گزینه A" value={optionA} onChange={(e) => setOptionA(e.target.value)} fullWidth sx={{ mb: 2 }} />
      <TextField label="گزینه B" value={optionB} onChange={(e) => setOptionB(e.target.value)} fullWidth sx={{ mb: 2 }} />
      <TextField label="گزینه C" value={optionC} onChange={(e) => setOptionC(e.target.value)} fullWidth sx={{ mb: 2 }} />
      <TextField label="گزینه D" value={optionD} onChange={(e) => setOptionD(e.target.value)} fullWidth sx={{ mb: 2 }} />

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="correct-option-label">گزینه صحیح</InputLabel>
        <Select labelId="correct-option-label" value={correctOption} label="گزینه صحیح" onChange={(e) => setCorrectOption(e.target.value)}>
          <MenuItem value="A">گزینه A</MenuItem>
          <MenuItem value="B">گزینه B</MenuItem>
          <MenuItem value="C">گزینه C</MenuItem>
          <MenuItem value="D">گزینه D</MenuItem>
        </Select>
      </FormControl>

      <TextField label="تاریخ انقضا" type="date" value={expirationDate} onChange={(e) => setExpirationDate(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />

      <Button variant="contained" fullWidth onClick={handleSubmit}>ثبت سؤال</Button>
    </Box>
  );
};

export default AddQuestionPage;
